import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "hooks/redux";
import axios, { CancelToken } from "axios";

import { GetTeeTime } from "api/rpc/2024-04/facilityAdmin/teesheet/teeSheet";
import { StatusCode } from "api/protocols";

import { capitalize, convertTime } from "helpers/Helpers";
import { showError } from "redux/actions/ui";

import Sheet from "components/sheet/Sheet";
import Spin from "components/spin/spin";
import { Badge } from "components/badge/Badge";

import { ITeeTimeNew, ITeeTime, IBookingCategory } from "redux/reducers/models/teetime";

interface ITeeTimeDetailsModal {
  open: boolean;
  teetime: ITeeTimeNew;
  bookingCategories: IBookingCategory[];
  onCancel: () => void;
}

export default function TeeTimeDetailsModal(props: ITeeTimeDetailsModal) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { open, teetime, bookingCategories, onCancel } = props;

  const [loadedTeeTime, setLoadedTeeTime] = useState<ITeeTime>(undefined);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadTeeTime(source.token);
    return () => source.cancel();
  }, []);

  async function loadTeeTime(token?: CancelToken) {
    if (!teetime) {
      return;
    }

    const teetimeRes = await GetTeeTime({ id: teetime.slots[0].tee_time_id }, false, token);

    if (teetimeRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tee time")));
      return;
    }

    setLoadedTeeTime(teetimeRes.data[0]);
  }

  function getTeeTimeType() {
    if (!loadedTeeTime) {
      return "Standard";
    }

    if (loadedTeeTime.league_id) {
      return "League";
    } else if (loadedTeeTime.tournament_id) {
      return "Tournament";
    } else if (loadedTeeTime.shotgun_application_id) {
      return "Shotgun";
    } else {
      return "Standard";
    }
  }

  return (
    <Sheet
      open={open}
      size={"small"}
      closable
      title={`${t("Tee Time -")} ${loadedTeeTime ? convertTime(loadedTeeTime?.start_time) : null}`}
      onCancel={() => onCancel()}
      cancelText={t("Close")}
    >
      <div className="teetime-modal-details">
        <p className="teetime-modal-details-options">{t("Details")}</p>
        <div className="flex mt-4 mb-4">
          <p className="teetime-modal-details-title">{t("Tee Time Type:")}</p>
          <p className="teetime-modal-details-options">{getTeeTimeType()}</p>
        </div>
        <div className="flex mt-4 mb-4">
          <p className="teetime-modal-details-title">{t("Booking Category:")}</p>

          {bookingCategories ? (
            <div className="teetime-modal-badge-container">
              {bookingCategories.map((category: any, index: number) => {
                let type: "primary" | "success" | "warning" | "error" | "gray" | "public";

                switch (category.title) {
                  case "Public":
                    type = "public";
                    break;
                  case "Member":
                    type = "error";
                    break;
                  case "GolfNow":
                    type = "warning";
                    break;

                  default:
                    type = "gray";
                    break;
                }

                return (
                  <Badge key={index} size="medium" type={type}>
                    {category.title}
                  </Badge>
                );
              })}
            </div>
          ) : (
            <div style={{ height: "25px", width: "25px" }}>
              <Spin />
            </div>
          )}
        </div>
        <div className="flex mt-4 mb-4">
          <p className="teetime-modal-details-title">{t("Cart Rule:")}</p>
          <p className="teetime-modal-details-options">{capitalize(loadedTeeTime?.cart_rule)}</p>
        </div>
        <div className="flex mt-4 mb-4">
          <p className="teetime-modal-details-title">{t("Payment Rule:")}</p>

          <div className="teetime-modal-badge-container">
            {loadedTeeTime?.credit_card_required ? (
              <Badge size="medium" type="gray">
                {t("Credit Card")}
              </Badge>
            ) : null}

            {loadedTeeTime?.pre_paid_required ? (
              <Badge size="medium" type="gray">
                {t("Prepaid")}
              </Badge>
            ) : null}

            {loadedTeeTime?.promotion_type && loadedTeeTime?.promotion_value != null ? (
              <Badge size="medium" type="success">
                {loadedTeeTime.promotion_label ||
                  (loadedTeeTime.promotion_type === "percent"
                    ? `${loadedTeeTime.promotion_value}% Off`
                    : `$${loadedTeeTime.promotion_value} Off`)}
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
    </Sheet>
  );
}
